import React, { useEffect, useState } from "react";
import {
  Card,
  Col,
  Row,
  Form,
  Input,
  Empty,
  Button,
  Space,
  Select,
  Divider,
  Alert,
  Skeleton,
  Radio,
  Flex,
  Tag,
} from "antd";

import useUpdate from "hooks/useUpdate";
import { MaskedInput } from "antd-mask-input";
import useFormErrors from "hooks/useFormErrors";
import useGet from "hooks/useGet";

import {
  building_number_regexp,
  apartment_number_regexp,
  name_with_numbers_regexp,
  name_regexp,
  special_characters_regexp,
  street_regexp,
} from "helpers/regexp";
import issue_statuses from "helpers/options/issue_statuses";
import dayjs from "dayjs";
import InputDate from "components/InputDate";
import service_date_delays from "helpers/options/service_date_delays";
import range from "helpers/range";
import getIssueType from "helpers/issue_types";
import {
  administrator_billing_status_options,
  worker_billing_status_options,
} from "helpers/options/billing_statuses";
import { useSettlement } from "context/providers/settlement";
import { useIssueKinds } from "context/providers/issue_kinds";
import Comments from "components/Comments";
import IssueCalendar from "components/IssueCalendar";
import IssueGallery from "components/IssueGallery";
import IssueSignature from "components/IssueSignature";
import IssueWorker from "components/IssueWorker";
import IssueFiles from "components/IssueFiles";

const disabledDateTime = () => ({
  disabledMinutes: () => [
    ...range(1, 15),
    ...range(16, 30),
    ...range(31, 45),
    ...range(46, 60),
  ],
});

const IssueForm = () => {
  const { is_changed, issue_id, setIsChanged, updateListIssue } =
    useSettlement();
  const { issue_kind_options } = useIssueKinds();
  const [nipKey, setNipKey] = useState(0);
  const [phoneKey, setPhoneKey] = useState(0);
  const [contactPhoneKey, setContactPhoneKey] = useState(0);
  const [postCodeKey, setPostCodeKey] = useState(0);
  const {
    is_loading,
    is_error: is_issue_error,
    data,
    refetch: refetchIssue,
  } = useGet({
    path: `/issues/${issue_id}`,
    context: "settlement",
    query: ["issue_settlement", issue_id],
    options: {
      enabled: Boolean(issue_id),
      cacheTime: 0,
    },
  });

  const [form] = Form.useForm();
  const { pristine, has_error, onFieldsChange, reset } = useFormErrors(data);

  const path = `/issues/${issue_id}`;

  const { is_saving, update } = useUpdate({
    path,
    form,
    onSuccess: refetchIssue,
  });

  useEffect(() => {
    setIsChanged(!pristine);
    // eslint-disable-next-line
  }, [pristine]);

  useEffect(() => {
    form.resetFields();
    reset();
    // eslint-disable-next-line
  }, [data]);

  const handleReset = () => {
    form.resetFields();
    reset();
    form.validateFields();
    setNipKey((k) => k + 1);
    setPhoneKey((k) => k + 1);
    setContactPhoneKey((k) => k + 1);
    setPostCodeKey((k) => k + 1);
  };

  if (!issue_id || is_issue_error) {
    return (
      <Card>
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Brak danych" />
      </Card>
    );
  }

  if (is_loading) {
    return (
      <Card title="Trwa pobieranie...">
        <Skeleton active />
      </Card>
    );
  }

  const type = getIssueType(data?.issue_type);

  return (
    <>
      <Form
        form={form}
        layout="vertical"
        onFieldsChange={onFieldsChange}
        initialValues={{
          created_by: data?.created_by,
          status: data?.status,
          issue_kind_id: data?.issue_kind?.id,
          service_date: data?.service_date ? dayjs(data.service_date) : null,
          service_date_delay: data?.service_date_delay,
          content: data?.content,
          client_first_name: data?.client_first_name,
          client_last_name: data?.client_last_name,
          client_street: data?.client_street,
          client_building_number: data?.client_building_number,
          client_apartment_number: data?.client_apartment_number,
          client_city: data?.client_city,
          client_post_code: data?.client_post_code,
          client_phone: data?.client_phone,
          client_email: data?.client_email,
          client_nip: data?.client_nip,
          client_company_name: data?.client_company_name,
          order_number: data?.order_number,
          order_date: data?.order_date ? dayjs(data.order_date) : null,
          fv_number: data?.fv_number,
          fv_date: data?.fv_date ? dayjs(data.fv_date) : null,
          annotation: data?.annotation,
          administration_reported_at: data?.administration_reported_at
            ? dayjs(data.administration_reported_at)
            : null,
          worker_billing_status: data?.worker_billing_status,
          administration_billing_status: data?.administration_billing_status,
          is_company: data?.is_company,
          client_contact_person_phone: data?.client_contact_person_phone || "",
        }}
        onFinish={async (values) => {
          const client_contact_person_phone = values.client_contact_person_phone
            ? values.client_contact_person_phone.replace(/_/g, "")
            : null;
          update({ ...values, client_contact_person_phone });
          updateListIssue(values);
        }}
      >
        {({ is_company }) => (
          <Card
            title={
              <Flex gap="0 12px" wrap>
                <span>
                  {`${data?.client_street} ${data?.client_building_number}${
                    data?.client_apartment_number
                      ? `/${data.client_apartment_number}`
                      : ""
                  }`}
                </span>
                <Tag color={type.color}>{type.label}</Tag>
              </Flex>
            }
            loading={is_loading}
            extra={
              <Button
                disabled={has_error || !is_changed}
                loading={is_loading || is_saving}
                type="primary"
                htmlType="submit"
              >
                Zapisz
              </Button>
            }
          >
            {is_changed && (
              <div
                style={{
                  position: "fixed",
                  left: "20px",
                  bottom: "20px",
                  zIndex: 100,
                }}
              >
                <Alert
                  message="UWAGA!"
                  description="Zapisz zmiany przed wyjściem!"
                  type="error"
                  showIcon
                  action={
                    <Space style={{ paddingLeft: 20 }}>
                      <Button
                        danger
                        type="primary"
                        htmlType="button"
                        onClick={handleReset}
                      >
                        Anuluj
                      </Button>
                      <Button
                        disabled={has_error}
                        loading={is_loading || is_saving}
                        type="primary"
                        htmlType="submit"
                      >
                        Zapisz
                      </Button>
                    </Space>
                  }
                />
              </div>
            )}
            <IssueWorker data={data} refetch={refetchIssue} />
            <Divider orientation="center" style={{ margin: "30px 0 40px" }}>
              Stan rozliczenia
            </Divider>
            <Row gutter={[12, 12]}>
              <Col xs={24} md={24} lg={12} xl={12}>
                <Form.Item
                  label="Pracownik"
                  name="worker_billing_status"
                  hasFeedback
                >
                  <Select
                    placeholder="Wybierz stan"
                    options={worker_billing_status_options}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={24} lg={12} xl={12}>
                <Form.Item
                  label="Administrator"
                  name="administration_billing_status"
                  hasFeedback
                >
                  <Select
                    placeholder="Wybierz stan"
                    options={administrator_billing_status_options}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Comments
              issue_id={issue_id}
              refetch={refetchIssue}
              data={data?.issue_comments || []}
              maxHeight={220}
            />
            <IssueGallery issue_id={issue_id} data={data?.issue_images || []} />
            <IssueFiles issue_id={issue_id} data={data?.issue_files || []} />
            <Divider orientation="center" style={{ margin: "30px 0 40px" }}>
              Informacje o zleceniu
            </Divider>
            <Row gutter={[12, 12]}>
              <Col xs={24} md={24} lg={12} xl={12}>
                <Form.Item
                  label="Adnotacja"
                  name="annotation"
                  hasFeedback
                  rules={[
                    {
                      pattern: special_characters_regexp,
                      message: "Pole zawiera niedozwolone znaki!!",
                    },
                  ]}
                >
                  <Input placeholder="Adnotacja" maxLength={128} />
                </Form.Item>
              </Col>
              <Col xs={24} md={24} lg={12} xl={12}>
                <Form.Item
                  label="Data zgłoszenia do administracji"
                  name="administration_reported_at"
                >
                  <InputDate />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={[12, 12]}>
              <Col xs={24} md={24} lg={12} xl={12}>
                <Form.Item
                  label="Numer zlecenia"
                  name="order_number"
                  hasFeedback
                  rules={[
                    {
                      pattern: special_characters_regexp,
                      message: "Pole zawiera niedozwolone znaki!!",
                    },
                  ]}
                >
                  <Input placeholder="Numer zlecenia" maxLength={128} />
                </Form.Item>
              </Col>
              <Col xs={24} md={24} lg={12} xl={12}>
                <Form.Item label="Data wystawionego zlecenia" name="order_date">
                  <InputDate />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={[12, 12]}>
              <Col xs={24} md={24} lg={12} xl={12}>
                <Form.Item
                  label="Numer FV"
                  name="fv_number"
                  hasFeedback
                  rules={[
                    {
                      pattern: special_characters_regexp,
                      message: "Pole zawiera niedozwolone znaki!!",
                    },
                  ]}
                >
                  <Input placeholder="Numer FV" maxLength={128} />
                </Form.Item>
              </Col>
              <Col xs={24} md={24} lg={12} xl={12}>
                <Form.Item label="Data wystawienia FV" name="fv_date">
                  <InputDate />
                </Form.Item>
              </Col>
            </Row>
            <Divider orientation="center" style={{ margin: "40px 0" }}>
              Informacje o kliencie
            </Divider>
            <Row gutter={[12, 12]} justify="center">
              <Col xl={24} lg={24} md={24} xs={24}>
                <Form.Item name="is_company">
                  <Radio.Group
                    style={{ width: "100%" }}
                    options={[
                      { label: "Osoba fizyczna", value: false },
                      { label: "Firma", value: true },
                    ]}
                    onChange={({ target: { value } }) =>
                      form.setFieldValue("is_company", value)
                    }
                    defaultValue={form.getFieldValue("is_company")}
                    optionType="button"
                    buttonStyle="solid"
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={[12, 12]}>
              <Col xs={24} md={24} lg={12} xl={12}>
                <Form.Item
                  label="Imię"
                  name="client_first_name"
                  hasFeedback
                  rules={[
                    {
                      pattern: name_with_numbers_regexp,
                      message: "Pole zawiera niedozwolone znaki!!",
                    },
                  ]}
                >
                  <Input
                    placeholder="Imię"
                    maxLength={128}
                    disabled={is_company}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={24} lg={12} xl={12}>
                <Form.Item
                  label="Nazwisko"
                  name="client_last_name"
                  hasFeedback
                  rules={[
                    {
                      pattern: name_with_numbers_regexp,
                      message: "Pole zawiera niedozwolone znaki!!",
                    },
                  ]}
                >
                  <Input
                    placeholder="Nazwisko"
                    maxLength={128}
                    disabled={is_company}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={[12, 12]}>
              <Col xs={12}>
                <Form.Item
                  label="Nazwa firmy"
                  name="client_company_name"
                  hasFeedback
                  rules={[
                    {
                      pattern: name_with_numbers_regexp,
                      message: "Pole zawiera niedozwolone znaki!",
                    },
                  ]}
                >
                  <Input
                    placeholder="Nazwa firmy"
                    maxLength={128}
                    disabled={!is_company}
                  />
                </Form.Item>
              </Col>
              <Col xs={12}>
                <Form.Item
                  label="NIP"
                  name="client_nip"
                  hasFeedback
                  rules={[{ pattern: /^\d{10}$/, message: "Niepoprawny NIP" }]}
                >
                  <MaskedInput
                    key={nipKey}
                    mask="0000000000"
                    onChange={({ target: { value } }) => {
                      form.setFieldValue("client_nip", value.replace(/_/g, ""));
                    }}
                    disabled={!is_company}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={[12, 12]}>
              <Col xs={24} md={24} lg={12} xl={12}>
                <Form.Item
                  label="Telefon do właściciela / administratora"
                  name="client_phone"
                  required
                  hasFeedback
                  rules={[
                    {
                      pattern: /^\d{9}$/,
                      message: "Niepoprawny telefon",
                    },
                  ]}
                >
                  <MaskedInput key={phoneKey} mask="000000000" />
                </Form.Item>
              </Col>
              <Col xs={24} md={24} lg={12} xl={12}>
                <Form.Item
                  label="Telefon do osoby w mieszkaniu"
                  name="client_contact_person_phone"
                  hasFeedback
                  tooltip="Telefon do osoby w mieszkaniu"
                  rules={[
                    {
                      pattern: /^\d{9}$/,
                      message: "Niepoprawny telefon",
                    },
                  ]}
                >
                  <MaskedInput key={contactPhoneKey} mask="000000000" />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={[12, 12]}>
              <Col xs={24} md={24} lg={12} xl={12}>
                <Form.Item
                  label="E-mail"
                  name="client_email"
                  hasFeedback
                  rules={[
                    {
                      type: "email",
                      message: "Niepoprawny e-mail",
                    },
                  ]}
                >
                  <Input placeholder="E-mail" maxLength={128} />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={[12, 12]}>
              <Col xs={12}>
                <Form.Item
                  label="Ulica"
                  name="client_street"
                  hasFeedback
                  rules={[
                    {
                      required: true,
                      message: "Ulica jest wymagane!",
                    },
                    {
                      pattern: street_regexp,
                      message: "Pole zawiera niedozwolone znaki!!",
                    },
                  ]}
                >
                  <Input placeholder="Ulica" maxLength={128} />
                </Form.Item>
              </Col>
              <Col xs={6}>
                <Form.Item
                  label="Nr bud."
                  name="client_building_number"
                  hasFeedback
                  rules={[
                    {
                      required: true,
                      message: "Numer budynku jest wymagany!",
                    },
                    {
                      pattern: building_number_regexp,
                      message:
                        "Pole zawiera niedozwolone znaki lub nie zaczyna się od cyfry!",
                    },
                  ]}
                >
                  <Input placeholder="Nr bud." maxLength={10} />
                </Form.Item>
              </Col>
              <Col xs={6}>
                <Form.Item
                  label="Nr miesz."
                  name="client_apartment_number"
                  hasFeedback
                  rules={[
                    {
                      required: true,
                      message:
                        "Numer mieszkania jest wymagany, w przypadku domu jednorodzinnego proszę wpisać -",
                    },
                    {
                      pattern: apartment_number_regexp,
                      message:
                        "Pole zawiera niedozwolone znaki lub nie zaczyna się od cyfry!",
                    },
                  ]}
                >
                  <Input placeholder="Nr miesz." maxLength={10} />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={[12, 12]}>
              <Col xs={12}>
                <Form.Item
                  label="Miasto"
                  name="client_city"
                  hasFeedback
                  rules={[
                    {
                      required: true,
                      message: "Miasto jest wymagane!",
                    },
                    {
                      pattern: name_regexp,
                      message: "Pole zawiera niedozwolone znaki!!",
                    },
                  ]}
                >
                  <Input placeholder="Miasto" maxLength={128} />
                </Form.Item>
              </Col>
              <Col xs={12}>
                <Form.Item
                  label="Kod pocztowy"
                  name="client_post_code"
                  hasFeedback
                  required
                  rules={[
                    {
                      pattern: /^\d{2}-\d{3}$/,
                      message: "Niepoprawny kod pocztowy",
                    },
                  ]}
                >
                  <MaskedInput
                    mask="00-000"
                    key={postCodeKey}
                    onChange={({ target: { value } }) => {
                      const clear_value = value?.replace(/_/g, "");
                      const new_value = clear_value === "-" ? "" : clear_value;
                      form.setFieldValue("client_post_code", new_value);
                    }}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Divider orientation="center" style={{ margin: "30px 0 40px" }}>
              Informacje o zgłoszeniu
            </Divider>
            <Row gutter={[12, 12]}>
              <Col xs={24} md={24} lg={12} xl={12}>
                <Form.Item label="Status" name="status" hasFeedback>
                  <Select placeholder="Status" options={issue_statuses} />
                </Form.Item>
              </Col>
              <Col xs={24} md={24} lg={12} xl={12}>
                <Form.Item label="Rodzaj" name="issue_kind_id" hasFeedback>
                  <Select placeholder="Rodzaj" options={issue_kind_options} />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={[12, 12]}>
              <Col xs={24} md={24} lg={12} xl={12}>
                <Row gutter={[12, 12]} align="middle">
                  <Col flex="auto">
                    <Form.Item label="Data umówienia" name="service_date">
                      <InputDate
                        format="DD-MM-YYYY HH:mm"
                        showTime={{ format: "HH:mm" }}
                        disabledTime={disabledDateTime}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </Col>
              <Col xs={24} md={24} lg={12} xl={12}>
                <Form.Item
                  label="Data umówienia (opóźnienie)"
                  name="service_date_delay"
                  tooltip="Dostępność klienta w podanym przedziale czasowym"
                >
                  <Select
                    placeholder="Data umówienia (opóźnienie)"
                    options={service_date_delays}
                    defaultValue={service_date_delays[0]}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item
              label="Opis zgłoszenia"
              name="content"
              hasFeedback
              rules={[
                {
                  required: true,
                  message: "Opis zgłoszenia jest wymagany!",
                },
                {
                  pattern: special_characters_regexp,
                  message: "Pole zawiera niedozwolone znaki!!",
                },
              ]}
            >
              <Input.TextArea
                placeholder="Opis zgłoszenia"
                maxLength={400}
                showCount={true}
                rows={7}
              />
            </Form.Item>
            <Divider />
            <Row gutter={[12, 12]} align="top" justify="space-evenly">
              <Col xs={12} md={9} lg={9} xl={9}>
                <IssueCalendar
                  origin_source="z rozliczeń"
                  issue_id={issue_id}
                  handleConfirm={refetchIssue}
                />
              </Col>
            </Row>
            <Divider />
            <IssueSignature data={data?.issue_signature} />
            <Row justify="end">
              <Space>
                <Button
                  disabled={has_error || !is_changed}
                  loading={is_loading || is_saving}
                  type="primary"
                  htmlType="submit"
                >
                  Zapisz
                </Button>
              </Space>
            </Row>
          </Card>
        )}
      </Form>
    </>
  );
};

export default IssueForm;
